import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { usePromiseTracker } from 'react-promise-tracker';
import {
  useGridApiRef,
  GridPinnedColumns,
  GridColumnVisibilityModel,
  GridSortModel,
  GridColumns as XGridColumns,
} from '@mui/x-data-grid-pro';
import { XGrid } from '@AMIEWEB/Common';
import _ from 'lodash';
import { userDevicePreferenceActions } from 'oidc/UserDevicePreference/userPreference.redux';
import { selectUserGridPreference, selectUserPreference } from 'oidc/UserDevicePreference/userPreference.selectors';
import { selectGridState } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.selectors';
import { gridStateActions } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.redux';
import { GridColumns } from 'app/models/GlobalSearch/GlobalSearch';
import { formatMultiSortColumns, formatMultiSortXGridColumns } from '@AMIEWEB/GlobalSearch/helper';
import { selectPlacementPayPackagesPagination } from '@AMIEWEB/Order/Store/Order.selectors';
import { usePlacementPayPackagesFetch } from '@AMIEWEB/Order/OrderDetails/PayPackages/PlacementPayPackages/usePlacementPayPackagesFetch';
import {
  defaultPinnedColumns,
  placementPayPackagesColumnVisibility,
  placementPayPackagesColumns,
} from '@AMIEWEB/Order/OrderDetails/PayPackages/PlacementPayPackages/utils';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import {
  placementMarginScenarioActions,
  placementMarginScenarioReducer,
  placementMarginScenarioSliceKey,
} from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.redux';
import { placementMarginScenarioSaga } from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.saga';
import {
  placementMarginPayPackageStatusActions,
  placementMarginPayPackageStatusSliceKey,
  placementMarginPayPackageStatusSliceReducer,
} from 'store/redux-store/margin-tool/slices/placement/placement-pay-package-status/placement-pay-package-status.redux';
import { placementMarginPayPackageStatusSaga } from 'store/redux-store/margin-tool/slices/placement/placement-pay-package-status/placement-pay-package-status.saga';
import {
  placementMarginToolEmailAction,
  placementMarginToolEmailReducer,
  placementMarginToolEmailSliceKey,
} from 'store/redux-store/margin-tool/slices/placement/placement-margin-tool-email/placement-margin-tool-email.redux';
import { placementMarginToolEmailSaga } from 'store/redux-store/margin-tool/slices/placement/placement-margin-tool-email/placement-margin-tool-email.saga';
import {
  placementDetailReducer,
  placementDetailsAction,
  placementDetailSliceKey,
} from 'store/redux-store/placement-details/slice';
import { placementDetailsSaga } from 'store/redux-store/placement-details/saga';
import { IEmailType } from 'app/models/Notification/Notification';

export const FacilityPlacementPayPackagesGrid = ({ facilityId }: { facilityId: number }) => {
  const gridTag = `facility-placement-pay-packages-grid-${facilityId}`;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const emailIconField = 'emailIcon';

  useInjectReducer({ key: placementMarginScenarioSliceKey, reducer: placementMarginScenarioReducer });
  useInjectSaga({ key: placementMarginScenarioSliceKey, saga: placementMarginScenarioSaga });
  useInjectReducer({ key: placementMarginPayPackageStatusSliceKey, reducer: placementMarginPayPackageStatusSliceReducer });
  useInjectSaga({ key: placementMarginPayPackageStatusSliceKey, saga: placementMarginPayPackageStatusSaga });
  useInjectReducer({ key: placementMarginToolEmailSliceKey, reducer: placementMarginToolEmailReducer });
  useInjectSaga({ key: placementMarginToolEmailSliceKey, saga: placementMarginToolEmailSaga });
  useInjectReducer({ key: placementDetailSliceKey, reducer: placementDetailReducer });
  useInjectSaga({ key: placementDetailSliceKey, saga: placementDetailsSaga });

  const { promiseInProgress } = usePromiseTracker({ area: 'get-Placement-Pay-Packages' });

  const sourceRows = useSelector((state: any) => state?.orderData?.placementPayPackagesSource ?? []);
  const pagination = useSelector(selectPlacementPayPackagesPagination);
  const fetchPlacementPayPackages = usePlacementPayPackagesFetch({ facilityId });

  const gridPreferences = useSelector(selectUserGridPreference).find(item => item.id === gridTag);
  const userPreference = useSelector(selectUserPreference);
  const gridState = useSelector(selectGridState);

  const [pendingEmail, setPendingEmail] = useState<null | { placementId: number; scenarioId?: number; variant: string }>(
    null,
  );

  const selectedScenario = useSelector((state: any) => state?.[placementMarginScenarioSliceKey]?.selectedScenario);
  const bookingPeriod = useSelector((state: any) => state?.[placementMarginScenarioSliceKey]?.bookingPeriod);
  const emailRecipients = useSelector((state: any) => state?.[placementMarginPayPackageStatusSliceKey]?.emailRecipients);
  const placementHeader = useSelector((state: any) => state?.[placementDetailSliceKey]?.header);

  const onSelectEmailVariant = useCallback(
    (row: any, variant: string) => {
      const placementId = Number(row?.placementId);
      const scenarioId = row?.scenarioId != null ? Number(row?.scenarioId) : undefined;
      if (!Number.isFinite(placementId) || placementId <= 0) {
        dispatch(
          globalActions.setSnackBar({
            message: 'Unable to email: missing placement id.',
            severity: 'error',
          }),
        );
        return;
      }

      setPendingEmail({ placementId, scenarioId, variant });

      dispatch(
        placementMarginScenarioActions.getPlacementMarginToolDetails({
          placementId,
          scenarioId: Number.isFinite(scenarioId) ? scenarioId : undefined,
        }),
      );
      dispatch(placementMarginPayPackageStatusActions.getPlacementMarginEmailRecipientsList({ placementId }));
      dispatch(placementDetailsAction.getHeaderDetailsAction({ placementId }));

      dispatch(
        placementMarginPayPackageStatusActions.setStatusUpdatedEmailModalOpen({
          openEmailModal: true,
          emailType: IEmailType.Manual,
          emailVariant: variant,
        }),
      );
    },
    [dispatch],
  );

  const [columns, setColumns] = useState(placementPayPackagesColumns(t, { onSelectEmailVariant }));
  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumns>(defaultPinnedColumns);
  const [currentSortModel, setCurrentSortModel] = useState<any[]>([]);
  const [currentColumnVisibilityModel, setCurrentColumnVisibilityModel] = useState<any>(
    _.cloneDeep(placementPayPackagesColumnVisibility),
  );

  const rows = sourceRows || [];

  const getSortModelPreferences = (sortModel: any[]) => {
    const columnPreferences = gridPreferences?.value;
    if (columnPreferences?.multiSortedColumns && Array.isArray(columnPreferences?.multiSortedColumns)) {
      return formatMultiSortXGridColumns(columnPreferences?.multiSortedColumns);
    }
    return sortModel;
  };

  useEffect(() => {
    if (gridPreferences?.value?.multiSortedColumns?.length > 0) {
      setCurrentSortModel(getSortModelPreferences(gridPreferences?.value?.multiSortedColumns));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridPreferences]);

  useEffect(() => {
    if (userPreference?.loaded) {
      dispatch(gridStateActions.setGrid(gridTag));
      determineVisibilityModel();
      determineColumn();
      determinePinnedColumns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPreference?.loaded, gridPreferences]);

  useEffect(() => {
    if (!pendingEmail) return;

    const headerReady = Number(placementHeader?.placementId) === Number(pendingEmail.placementId);
    const recipientsReady = emailRecipients != null;
    const scenarioReady =
      Number(selectedScenario?.placementId) === Number(pendingEmail.placementId) &&
      (pendingEmail.scenarioId == null || Number(selectedScenario?.scenarioId) === Number(pendingEmail.scenarioId));
    const bookingReady =
      bookingPeriod == null ||
      Number(bookingPeriod?.placementId) === Number(pendingEmail.placementId) ||
      Number(bookingPeriod?.placementId) === 0;

    if (headerReady && recipientsReady && scenarioReady && bookingReady) {
      dispatch(placementMarginToolEmailAction.handlePlacementMarginToolEmailCreation());
      setPendingEmail(null);
    }
  }, [
    pendingEmail,
    placementHeader?.placementId,
    emailRecipients,
    selectedScenario?.scenarioId,
    selectedScenario?.placementId,
    bookingPeriod?.placementId,
    dispatch,
  ]);

  const refreshPlacementPayPackages = () => {
    fetchPlacementPayPackages({
      pageNumber: gridState?.pageNumber || 1,
      pageSize: gridState?.pageSize || 20,
      sortModel: currentSortModel,
    });
  };

  const normalizePinnedColumns = useCallback(
    (value: GridPinnedColumns | undefined | null): GridPinnedColumns => {
      const left = Array.isArray(value?.left) ? [...value!.left] : [];
      const right = Array.isArray(value?.right) ? [...value!.right] : [];
      const leftWithoutEmail = left.filter(f => f !== emailIconField);
      return {
        left: [emailIconField, ...leftWithoutEmail],
        right,
      };
    },
    [emailIconField],
  );

  const normalizeColumns = useCallback(
    (cols: any[]) => {
      const emailCol = cols.find(c => c?.field === emailIconField);
      const rest = cols.filter(c => c?.field !== emailIconField);
      return emailCol ? [emailCol, ...rest] : cols;
    },
    [emailIconField],
  );

  const handlePinnedColumnsChange = useCallback(
    (updatedPinnedColumns: GridPinnedColumns) => {
      const normalized = normalizePinnedColumns(updatedPinnedColumns);
      setPinnedColumns(normalized);
      const preferenceData = {
        id: gridTag,
        value: {
          ...(gridPreferences?.value || {}),
          pinnedColumns: normalized,
        },
      };
      dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
    },
    [dispatch, gridTag, gridPreferences?.value, normalizePinnedColumns],
  );

  const resetColumns = () => {
    const defaultCols = placementPayPackagesColumns(t, { onSelectEmailVariant });
    setColumns(defaultCols);
    setCurrentSortModel([]);
    setCurrentColumnVisibilityModel(placementPayPackagesColumnVisibility);
    setPinnedColumns(defaultPinnedColumns);

    const preferenceData = {
      id: gridTag,
      value: {
        ...(gridPreferences?.value || {}),
        columns: defaultCols,
        columnVisibilityModel: placementPayPackagesColumnVisibility,
        pinnedColumns: defaultPinnedColumns,
        multiSortedColumns: [],
      },
    };
    dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
    dispatch(
      globalActions.setSnackBar({
        message: 'Columns have been reset.',
        severity: 'success',
      }),
    );
  };

  const onSortModelChanged = (model: GridSortModel) => {
    saveSortModel(model);
    setCurrentSortModel(model);
    fetchPlacementPayPackages({
      pageNumber: 1,
      pageSize: gridState?.pageSize || 20,
      sortModel: model,
    });
  };

  const saveSortModel = useCallback(
    sortModel => {
      const multiSortedColumns = sortModel?.length > 0 ? formatMultiSortColumns(sortModel) : undefined;
      if (!_.isEqual(multiSortedColumns, gridState?.multiSortedColumns)) {
        const preferenceData = {
          id: gridTag,
          value: {
            ...(gridPreferences?.value || {}),
            multiSortedColumns: multiSortedColumns,
          },
        };
        dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gridPreferences?.value, columns?.length, gridState?.multiSortedColumns, currentColumnVisibilityModel],
  );

  const savePreferences = useCallback(
    ({
      columns: gridColumns,
      visibilityModel,
    }: {
      field?: string;
      columns: XGridColumns;
      visibilityModel: GridColumnVisibilityModel;
    }) => {
      if (!columns || columns.length === 0 || !userPreference.loaded) return;
      const currentGridColumns = new GridColumns(gridColumns).columns;
      if (currentGridColumns?.length > 0 && !currentGridColumns[0].field) currentGridColumns.shift();

      const comparableColumns = new GridColumns(
        gridPreferences?.value?.columns,
        gridPreferences?.value?.columnVisibilityModel,
      );
      const columnsEqual = comparableColumns.isEqual(currentGridColumns);
      const visibilityEqual = comparableColumns.isVisibilityModelEqual(visibilityModel);

      if (!columnsEqual || !visibilityEqual) {
        const preferenceData = {
          id: gridTag,
          value: {
            ...(gridPreferences?.value || {}),
            columns: currentGridColumns,
            columnVisibilityModel: visibilityModel,
          },
        };
        dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
      }
    },
    [userPreference.loaded, gridPreferences?.value, dispatch, columns],
  );

  const getVisibilityPreferences = (cols: any) => {
    const columnPreferences = gridPreferences?.value;
    if (columnPreferences?.columnVisibilityModel) {
      return columnPreferences?.columnVisibilityModel;
    }
    return cols;
  };

  const determineVisibilityModel = () => {
    const currentVisibilityModel = getVisibilityPreferences(placementPayPackagesColumnVisibility);
    setCurrentColumnVisibilityModel(currentVisibilityModel);
  };

  const determinePinnedColumns = () => {
    const pinnedColumn = (gridPreferences?.value?.pinnedColumns || defaultPinnedColumns) as GridPinnedColumns;
    setPinnedColumns(normalizePinnedColumns(pinnedColumn));
  };

  const determineColumn = () => {
    const currentColumn = normalizeColumns(getColumnPreferences(columns));
    setColumns(currentColumn);
  };

  const getColumnPreferences = (cols: any[]) => {
    try {
      const columnPreferences = gridPreferences?.value;
      if (columnPreferences?.columns) {
        const newColumns = cols.map(column => {
          const foundIndex = columnPreferences.columns.findIndex(item => item?.field === column?.field);
          if (foundIndex !== -1) {
            return {
              ...column,
              ...columnPreferences.columns[foundIndex],
            };
          }
          return column;
        });
        return newColumns.sort((a, b) => {
          const indexA = columnPreferences.columns.findIndex(i => i.field === a.field);
          const indexB = columnPreferences.columns.findIndex(i => i.field === b.field);
          return (indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA) - (indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB);
        });
      }
      return cols;
    } catch {
      return cols;
    }
  };

  return (
    <XGrid
      getRowId={row => row.id}
      gridTag={gridTag}
      columns={columns}
      apiRef={apiRef}
      rows={rows}
      autoHeight
      disableSelectionOnClick
      loading={promiseInProgress}
      pinnedColumns={pinnedColumns}
      onPinnedColumnsChange={handlePinnedColumnsChange}
      defaultSortModel={currentSortModel}
      sortModel={currentSortModel}
      onSortModelChange={onSortModelChanged}
      paginationMode="server"
      rowCount={pagination.totalCount}
      rowCountDisplay={pagination.totalCount}
      onPageChange={(page, details) => {
        fetchPlacementPayPackages({ pageNumber: page + 1, pageSize: gridState?.pageSize || 20 });
      }}
      onPageSizeChange={(size, details) => {
        fetchPlacementPayPackages({ pageNumber: 1, pageSize: size });
      }}
      defaultColumnVisibilityModel={placementPayPackagesColumnVisibility}
      disableMultipleColumnsSorting={false}
      columnVisibilityModel={currentColumnVisibilityModel}
      onColumnVisibilityModelChange={(model, details) => {
        const normalizedModel = { ...model, [emailIconField]: true };
        dispatch(gridStateActions.setColumnVisibilityModel(normalizedModel));
        setCurrentColumnVisibilityModel(_.cloneDeep(normalizedModel));
        savePreferences({
          field: undefined,
          columns: _.cloneDeep(apiRef?.current?.getAllColumns()),
          visibilityModel: _.cloneDeep(normalizedModel),
        });
      }}
      onColumnOrderChange={(params, event, details, _columns) => {
        savePreferences({
          field: params.field,
          columns: _columns,
          visibilityModel: _.cloneDeep(currentColumnVisibilityModel ?? placementPayPackagesColumnVisibility),
        });
      }}
      onColumnResizeStop={_columns => {
        savePreferences({
          field: undefined,
          columns: _columns,
          visibilityModel: _.cloneDeep(currentColumnVisibilityModel ?? placementPayPackagesColumnVisibility),
        });
      }}
      toolbarProps={{
        refreshButton: true,
        filterButton: true,
        resetButton: true,
        refreshGrid: () => refreshPlacementPayPackages(),
        resetColumns: resetColumns,
      }}
    />
  );
};
